import { useMemo, useState } from 'react'
import { MdTabs } from '@/components/ui'
import { useHasAnyPermission } from '@/shell/module-system'
import { UvivaSubmissionsPage } from './pages/UvivaSubmissionsPage'
import { UvivaInventoryPage } from './pages/UvivaInventoryPage'
import { UvivaOperationsPage } from './pages/UvivaOperationsPage'

type Tab = 'submissions' | 'inventory' | 'operations'

const TABS = [
  { id: 'submissions' as Tab, label: 'Submissions' },
  { id: 'inventory' as Tab, label: 'Inventory' },
  { id: 'operations' as Tab, label: 'Operations' },
]

function getInitialTab(availableTabs: readonly Tab[]): Tab {
  const params = new URLSearchParams(window.location.search)
  const t = params.get('tab')
  if (t && availableTabs.includes(t as Tab)) return t as Tab
  return availableTabs[0]
}

export function UvivaPage() {
  // Regulatory (CofA) + finance (Invoice) get read-only submission visibility
  // only — Inventory/Operations are BI/reporting views outside that scope,
  // and their backend endpoints stay gated to admin/operations/uviva.
  const fullAccess = useHasAnyPermission(['admin', 'operations', 'purchasing', 'uviva'])
  const availableTabs = useMemo(
    () => (fullAccess ? TABS : TABS.filter((t) => t.id === 'submissions')),
    [fullAccess],
  )

  const [tab, setTab] = useState<Tab>(() => getInitialTab(availableTabs.map((t) => t.id)))

  function handleTabChange(next: Tab) {
    setTab(next)
    const url = new URL(window.location.href)
    url.searchParams.set('tab', next)
    window.history.replaceState(null, '', url.toString())
  }

  return (
    <div className="flex h-full flex-col bg-canvas">
      {availableTabs.length > 1 ? (
        <MdTabs tabs={availableTabs} value={tab} onChange={handleTabChange} className="shrink-0" />
      ) : null}
      <div className="min-h-0 flex-1 overflow-hidden">
        {tab === 'submissions' && <UvivaSubmissionsPage />}
        {tab === 'inventory' && <UvivaInventoryPage />}
        {tab === 'operations' && <UvivaOperationsPage />}
      </div>
    </div>
  )
}
